import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { last as _last } from 'lodash/fp'

import {
  EMPTY,
  catchError,
  delay,
  filter,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'

import { ConversationApiService } from '@flowx/designer/modules/ai/common/api/conversation-api.service'
import {
  ConversationOutcomeClassification,
  ContextItem,
  ConversationThreadContextDataItemType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  ContextDataKeys,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { ConversationErrorActions } from '@flowx/designer/modules/ai/conversation/conversation-error.actions'
import {
  AiChatBubbleActions,
  ConversationActions,
  TypeAreaActions,
} from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { BpmnAiAnalystService } from '@flowx/designer/modules/bpmn-editor/bpmn-ai-analyst.service'
import { AppNavigationService } from '@flowx/designer/shared/app-navigation/app-navigation.service'
import { NavigationActions } from '@flowx/designer/shared/app-navigation/app-navigation.service'
import { AreaStores } from '@flowx/designer/shared/effector/area/area.stores'
import { NodeStores } from '@flowx/designer/shared/effector/node/node.stores'
import { SequenceStores } from '@flowx/designer/shared/effector/sequence/sequence.stores'
import { ActivityNode } from '@flowx/designer/shared/entities/node.types'
import { BulkUpdateProcessEntitiesResponse } from '@flowx/designer/shared/entities/process-api.types'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectAppVersionId,
  selectQueryParams,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { AiAnalystActions } from './analyst.actions'

import { ConversationSelectors } from '../../../conversation/conversation.selectors'
import {
  AiActions,
  AiConversationServiceActions,
  SequenceMenuActions,
} from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'
import {
  AiProcessEntities,
  DummyAiDelayTime,
  GenerateFromSequenceRequest,
  GenerateProcessRequest,
  GenerateProcessResponse,
} from '../../../types/ai.types'
import { AiAnalystApi } from '../api/analyst.api'
import { AiAnalystUtils } from '../services/analyst.service'

@Injectable()
export class AiAnalystEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #analystApi = inject(AiAnalystApi)
  #bpmnAiAnalystService = inject(BpmnAiAnalystService)
  #aiConversationService = inject(AiConversationService)
  #conversationApiService = inject(ConversationApiService)
  #areasStore = inject(AreaStores)
  #router = inject(Router)
  #appNavigationService = inject(AppNavigationService)
  #toastMessageService = inject(ToastMessageService)

  generate = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generate),
      withLatestFrom(this.#store.select(AiSelectors.startedFromSequence)),
      map(([prompt, startedFromSequence]) => {
        if (startedFromSequence) {
          return AiAnalystActions.insertInProcess(prompt)
        } else {
          return AiAnalystActions.generateProcess(prompt)
        }
      })
    )
  )

  generateProcessFromDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcessFromDocument),
      switchMap(({ documentContextReference }) =>
        this.#analystApi.extractProcessFromDocument(documentContextReference).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiAnalystActions.generateProcess({ prompt: response.response })),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateProcessFromImages = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcessFromImages),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ files, prompt }, sessionId]) =>
        this.#mapImagePromptToFormData(files[0], prompt, sessionId)
      ),
      switchMap((formData) =>
        this.#analystApi.generateProcessFromImages(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => {
            if (response.image_type?.image_type !== 'business_process') {
              throw new Error(
                `The image cannot be used for process generation. Please upload a image with business process. \n \n ${response.image_type?.summary} \n \n`
              )
            }
            return AiAnalystActions.generateProcessSuccess(
              this.#mapGenerateProcessResponse(response)
            )
          }),
          catchError(({ status, error, message }) =>
            of(
              AiAnalystActions.generateProcessFailure({
                statusCode: status,
                errorMsg: message ?? error,
              })
            )
          )
        )
      )
    )
  )

  generateProcessFromBpmnFiles = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcessFromBpmnFiles),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ files }, sessionId]) => this.#mapBpmnFileToFormData(files[0], sessionId)),
      switchMap((formData) =>
        this.#analystApi.extractProcessFromBpmnFile(formData).pipe(
          delay(DummyAiDelayTime),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) =>
            AiAnalystActions.generateProcessSuccess(this.#mapGenerateProcessResponse(response))
          ),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.generateProcess),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId)
      ),
      map(
        ([{ prompt }, sessionId, requestId]) =>
          ({
            question: prompt,
            context: AiAnalystUtils.getInitialProcessContext(),
            session_id: sessionId,
            request_id: requestId,
          }) satisfies GenerateProcessRequest
      ),
      switchMap((payload) =>
        this.#analystApi.generateProcess(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) =>
            AiAnalystActions.generateProcessSuccess(this.#mapGenerateProcessResponse(response))
          ),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  insertInProcess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.insertInProcess),

      withLatestFrom(
        this.#store.select(AiSelectors.generatedSequenceProcess),
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.requestId),
        this.#store.select(AiSelectors.startedFromSequence)
      ),

      map(([{ prompt }, generatedProcess, sessionId, requestId, startedFromSequence]) => {
        const processState = AiAnalystUtils.getProcessStateSnapshot()
        const initialContext = AiAnalystUtils.getInitialProcessSequenceContext(
          processState,
          startedFromSequence
        )
        return {
          payload: {
            question: prompt,
            context: generatedProcess ?? initialContext.context,
            start_node_id: initialContext.startNodeId,
            end_node_id: initialContext.endNodeId,
            offset: initialContext.offset,
            session_id: sessionId,
            request_id: requestId,
          } satisfies GenerateFromSequenceRequest,
          params: {
            initialContext,
            processState,
          },
        }
      }),

      switchMap(({ payload, params }) =>
        this.#analystApi.insert(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => {
            let aiEntities: AiProcessEntities

            try {
              aiEntities = AiAnalystUtils.mergeUpdatesWithExistingProcess(params.processState, {
                aiProcess: response.answer,
                startNodeId: params.initialContext.startNodeId,
                endNodeId: params.initialContext.endNodeId,
                startNodeSwimlaneUuid: params.initialContext.startSwimlaneUuid,
                endNodeSwimlaneUuid: params.initialContext.endSwimlaneUuid,
                swimlanesUuidToId: params.initialContext.swimlanesUuidToId,
              })
            } catch (error) {
              if (!response.chat) {
                console.error(error)
                throw new Error('I couldn’t generate a valid response. Please try again.')
              }
            }

            return AiAnalystActions.generateProcessSuccess({
              response,
              aiEntities,
            })
          }),
          catchError(({ status, error }) =>
            of(AiAnalystActions.generateProcessFailure({ statusCode: status, errorMsg: error }))
          )
        )
      )
    )
  )

  generateFromSequence = createEffect(() =>
    this.#actions$.pipe(
      ofType(SequenceMenuActions.generateProcess),
      switchMap(({ sequenceId }) => {
        const processContext = AiAnalystUtils.getInitialProcessContext()
        const selectedSequence = SequenceStores.sequences$.getState().get(sequenceId)
        const startNode = NodeStores.nodeDefinitions$.getState().get(selectedSequence.startNodeId)
        const endNode = NodeStores.nodeDefinitions$.getState().get(selectedSequence.endNodeId)

        const executionPlanStep = {
          message: 'Describe the changes you want to make to the process.',
          context: {
            items: [
              {
                key: ContextDataKeys.AiProcessContext,
                itemType: ConversationThreadContextDataItemType.GenericString,
                valueGenericString: JSON.stringify(processContext),
              },
            ],
          },
          target: {
            name: null,
            targetType: null,
          },
          userPromptRequired: true,
          agent: {
            agent: AgentName.AiAnalyst,
            action: AgentAction.EditProcessBetweenNodes,
          },
          hintContextItems: [
            {
              key: ContextDataKeys.ProcessStartNodeId,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: startNode.id.toString(),
            },
            {
              key: ContextDataKeys.ProcessEndNodeId,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: endNode.id.toString(),
            },
          ],
        }

        return this.#aiConversationService.implementHintedAction(executionPlanStep)
      })
    )
  )

  onGenerateProcessSuccess = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.generateProcessSuccess),
        tap(({ aiEntities }) => this.#bpmnAiAnalystService.renderAiProcessEntities(aiEntities))
      ),
    { dispatch: false }
  )

  discardProcessDraft = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.discardProcessDraft),
        tap(() => this.#bpmnAiAnalystService.discardDraft())
      ),
    { dispatch: false }
  )

  keepOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.keepOutcome),
      switchMap(() => {
        const outcome = this.#store.selectSignal(ConversationSelectors.aiGeneratedProcessOutcome)()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()

        if (!outcome?.aiEntities) {
          const error = new Error('No AI entities found to keep in analyst outcome')
          console.error('[Conversation Error] Keep analyst outcome failed:', error)
          return of(
            ConversationErrorActions.keepOutcomeFailed({
              error,
              threadId,
              conversationId,
            })
          )
        }

        return this.#bpmnAiAnalystService.keepDraft(outcome.aiEntities).pipe(
          map((response: BulkUpdateProcessEntitiesResponse) =>
            AiAnalystActions.keepOutcomeSuccess({ response })
          ),
          catchError((err) => {
            const error = err instanceof Error ? err : new Error('Failed to keep analyst outcome')
            console.error('[Conversation Error] Keep analyst outcome failed:', error)
            return of(
              ConversationErrorActions.keepOutcomeFailed({
                error,
                threadId,
                conversationId,
              })
            )
          })
        )
      })
    )
  )

  keepOutcomeSuccess = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.keepThreadApiResponse),
        filter(({ agent }) => agent.agent === AgentName.AiAnalyst),
        switchMap(() => {
          const addedNodes = this.#store.selectSignal(ConversationSelectors.aiGeneratedNodes)()
          const addedUserTasksCount = addedNodes.filter(
            (node) => node.type === ActivityNode.USER_TASK
          ).length

          if (addedUserTasksCount > 0) {
            return this.#aiConversationService.addSimpleThread({
              message:
                'The process has been saved. For properly seeing the UI, I recommend you add navigation areas. As a starting point, I can put each User Task in a page.',
              contextData: {
                items: [
                  {
                    key: ContextDataKeys.UiAssignPagesToUserTasks,
                    itemType: ConversationThreadContextDataItemType.GenericString,
                    valueGenericString: 'true',
                  },
                ],
              },
              agent: { agent: AgentName.ConnectedGraph, action: AgentAction.NoopStore },
            })
          }
          return EMPTY
        })
      ),
    { dispatch: false }
  )

  generateProcessStreamEnded = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiConversationServiceActions.conversationMessageStreamSuccess),
      withLatestFrom(
        this.#store.select(ConversationSelectors.activeAgentThread),
        this.#store.select(ConversationSelectors.errorOutcomeDetails)
      ),
      filter(([, , errorDetails]) => !errorDetails?.hasError),
      map(([data, activeThread]) => {
        const message = activeThread?.messages.find((message) => message.id === data.messageId)
        const agent = message?.executionPlan.actions[0]?.agent.agent

        return {
          activeThread,
          message,
          agent,
        }
      }),
      filter(
        ({ agent, message }) =>
          agent === AgentName.AiAnalyst &&
          message?.outcome?.outcomes.some(
            (outcome) => outcome.outcomeClassification !== ConversationOutcomeClassification.Error
          )
      ),
      map(({ activeThread, message }) => {
        let aiEntities: AiProcessEntities
        if (!message?.outcome?.outcomes[0]?.textual?.content) {
          const error = new Error('No textual content found in analyst outcome response')
          console.error('[Conversation Error] Analyst outcome parsing failed:', error)
          return ConversationErrorActions.agentOutcomeParsingFailed({
            error,
            agentName: AgentName.AiAnalyst,
            messageId: message.id,
            threadId: activeThread?.id,
          })
        }

        try {
          aiEntities = AiAnalystUtils.extractAiEntitiesFromResponse(
            { answer: JSON.parse(message.outcome.outcomes[0].textual.content) },
            AiAnalystUtils.getProcessStateSnapshot()
          )
        } catch (e) {
          const error =
            e instanceof Error
              ? e
              : new Error('Could not extract BPMN changes from analyst outcome')
          console.error('[Conversation Error] Analyst outcome parsing failed:', error)
          return ConversationErrorActions.agentOutcomeParsingFailed({
            error,
            agentName: AgentName.AiAnalyst,
            messageId: message.id,
            threadId: activeThread?.id,
          })
        }

        const processStateSnapshot = AiAnalystUtils.getProcessStateSnapshot()
        const changes = AiAnalystUtils.getProcessChanges(
          aiEntities,
          processStateSnapshot.nodes,
          processStateSnapshot.boundaryEvents
        )
        const messageText =
          'Process has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.'

        return AiAnalystActions.summaryChangesReceived({
          messageText,
          contextDataItems: [
            ...message.contextData.items.filter(
              (item) =>
                ![
                  ContextDataKeys.UIMessageType,
                  ContextDataKeys.UIAiAnalystSummaryChanges,
                ].includes(item.key as ContextDataKeys)
            ),
            {
              key: ContextDataKeys.UIMessageType,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: MessageType.SystemMsg,
            },
            {
              key: ContextDataKeys.UIAiAnalystSummaryChanges,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: JSON.stringify(changes),
            },
          ] as ContextItem[],
        })
      })
    )
  )

  processGenerated = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.summaryChangesReceived),
        switchMap(({ messageText, contextDataItems }) =>
          this.#aiConversationService.addStoreMessage(
            {
              prompt: messageText,
              type: MessageType.SystemMsg,
            },
            contextDataItems
          )
        )
      ),
    { dispatch: false }
  )

  processTypeAreaSubmit = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(TypeAreaActions.promptSubmit),
        withLatestFrom(
          this.#store.select(ConversationSelectors.currentMessageAgent),
          this.#store.select(ConversationSelectors.activeAgentTarget),
          this.#store.select(ConversationSelectors.selectedProcessId)
        ),
        map(([, agent, target, selectedProcessId]) => ({
          agent,
          target,
          selectedProcessId,
        })),
        filter(
          ({ agent, target }) =>
            agent === AgentName.AiDesigner && target.targetType != null
        ),
        switchMap(({ selectedProcessId }) => {
          const appId = this.#store.selectSignal(selectAppId)()
          const appVersionUuid = this.#store.selectSignal(selectAppVersionId)()

          return this.#appNavigationService.execute({
            id: NavigationActions.openProcess,
            data: {
              processId: selectedProcessId,
              processVersionUuid: selectedProcessId,
              appId,
              appVersionUuid,
            },
          })
        })
      ),
    { dispatch: false }
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAnalystActions.sendFeedback),
        switchMap(({ payload }) => this.#analystApi.sendFeedback(payload))
      ),
    { dispatch: false }
  )

  assignPagesToUserTasks = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiChatBubbleActions.assignPagesToUserTasks),
      switchMap(() => {
        const currentQueryParams = this.#store.selectSignal(selectQueryParams)()
        return this.#aiConversationService
          .addStoreMessage({
            prompt: 'Assign pages to user tasks',
            type: MessageType.UserMsg,
          })
          .pipe(
            switchMap(() =>
              this.#router.navigate([], {
                queryParams: { ...currentQueryParams, showNavigationView: true, platform: 'web' },
                queryParamsHandling: 'merge',
              })
            ),
            tap(() => this.#store.dispatch(AiAnalystActions.beginAssigningPagesToUserTasks())),
            switchMap(() => this.#waitForAreasToLoad()),
            switchMap(() => {
              const nodes = this.#store.selectSignal(nodesSelector)()
              const aiGeneratedNodes = this.#store.selectSignal(
                ConversationSelectors.aiGeneratedNodes
              )()
              return AiAnalystUtils.createAreasForAllUserTasks(
                nodes,
                aiGeneratedNodes,
                this.#areasStore
              )
            }),
            switchMap(() =>
              this.#aiConversationService.addStoreMessage({
                prompt:
                  'Default navigation areas have been configured. You can make changes in the panel from process designer.',
                type: MessageType.SystemMsg,
              })
            ),
            map(() => AiAnalystActions.assignPagesToUserTasksSuccess())
          )
      }),
      catchError((error) => {
        console.error('[Conversation Error] Assign pages to user tasks failed:', error)
        this.#toastMessageService.showErrorMessage('Failed to assign pages to user tasks')
        return of(AiAnalystActions.assignPagesToUserTasksFailed())
      })
    )
  )

  assignPagesToUserTasksSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.assignPagesToUserTasksSuccess),
      switchMap(() => {
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const activeThread = this.#store.selectSignal(
          ConversationSelectors.activeNonDefaultThread
        )()

        if (!conversationId || !activeThread?.id) {
          console.error(
            'Expected to have conversation and thread when discarding assign pages to user tasks thread'
          )

          return EMPTY
        }

        return this.#conversationApiService
          .discardThread(conversationId, activeThread.id, {
            messageId: _last(activeThread.messages).id,
            shouldFinalize: false,
          })
          .pipe(
            map(() =>
              ConversationActions.discardThreadApiResponse({
                conversationId,
                threadId: activeThread.id,
              })
            )
          )
      })
    )
  )

  discardOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAnalystActions.discardOutcome),
      tap(() => this.#bpmnAiAnalystService.discardDraft()),
      map(() => AiAnalystActions.discardOutcomeSuccess())
    )
  )

  async #waitForAreasToLoad(): Promise<void> {
    return new Promise((resolve) => {
      const unsubscribe = this.#areasStore.areasEffects.loadAreasFx.doneData.watch((loaded) => {
        if (loaded) {
          unsubscribe()
          resolve()
        }
      })
    })
  }

  #mapBpmnFileToFormData(bpmnFile: File, sessionId: string): FormData {
    const formData = new FormData()
    formData.append('bpmn_file', bpmnFile)
    formData.append('session_id', sessionId)
    return formData
  }

  #mapImagePromptToFormData(imageFile: File, prompt: string, sessionId: string): FormData {
    const formData = new FormData()
    formData.append('image_file', imageFile)
    formData.append('prompt', prompt)
    formData.append('session_id', sessionId)
    return formData
  }

  #mapGenerateProcessResponse(response: GenerateProcessResponse): {
    response: GenerateProcessResponse
    aiEntities: AiProcessEntities
  } {
    let aiEntities: AiProcessEntities
    try {
      aiEntities = AiAnalystUtils.extractAiEntitiesFromResponse(
        response,
        AiAnalystUtils.getProcessStateSnapshot()
      )
    } catch (e) {
      if (!response.chat) {
        console.error(e)
        throw new Error('I couldn’t generate a valid response. Please try again.')
      }
    }

    return {
      response,
      aiEntities,
    }
  }
}
